import { Transform } from '../../engine/core/Transform';
import { Vec3 } from '../../engine/math/vec3';
import { AABB } from '../../engine/math/aabb';
import { Rng } from '../../engine/math/random';
import { prism, cylinder, box, combine } from '../../engine/geo/primitives';
import { translate, setEmissive } from '../../engine/geo/mesh';
import type { Ctx } from '../Ctx';
import type { Particles } from '../fx/Particles';
import { PALETTE, BIOMES, SPOTS } from '../world/Layout';
import { worldHeight } from '../world/Terrain';
import { instanced, jitterInstance, boulderMesh, Instance } from '../world/Props';

export interface DunesHandles {
  /** Wind-blown sand motes near the player. */
  update(playerPos: Vec3, particles: Particles, dt: number): void;
  cageCenter: Vec3;
}

/** Glass Dunes (§2.3): the Obelisk (tall transparent prism), the iron cage with the Black Key, scattered rocks. */
export function buildDunes(ctx: Ctx): DunesHandles {
  const P = PALETTE.dunes;
  const rng = new Rng(303);
  const st = ctx.statics;
  const b = BIOMES.dunes;
  const h = worldHeight;

  // Obelisk: a 26 m transparent prism, visible from anywhere in the biome
  const o = SPOTS.obelisk;
  const oy = h(o.x, o.z);
  const pts: [number, number][] = [];
  for (let i = 0; i < 6; i++) { const a = (i / 6) * Math.PI * 2; pts.push([Math.cos(a) * 1.6, -Math.sin(a) * 1.6]); }
  const shaft = prism(pts, 26, [0.75, 0.95, 1.0]);
  setEmissive(shaft, [0.25, 0.5, 0.6], 0.5);
  const cap = translate(cylinder(0, 1.9, 3, 6, [0.8, 0.97, 1.0]), 0, 27.5, 0);
  setEmissive(cap, [0.3, 0.6, 0.7], 0.8);
  const ot = new Transform();
  ot.position.set(o.x, oy, o.z);
  ot.snapPrev();
  ctx.spawn('dunes.obelisk', ot, combine(shaft, cap), { transparent: true, alpha: 0.42, castShadow: true });
  // opaque plinth so the base reads
  const plinth = translate(cylinder(3.2, 3.8, 1.0, 8, P[2]), o.x, oy + 0.4, o.z);
  st.addCylinder(o.x, o.z, 1.7, oy - 1, oy + 26, 'obelisk');
  st.addFloor(AABB.fromMinMax(o.x - 3.2, oy - 1, o.z - 3.2, o.x + 3.2, oy + 0.9, o.z + 3.2), 'plinth');

  // iron cage at the base (+Z side): 10 bars in a 1.5 m ring; the key sits inside
  const cage = new Vec3(o.x, oy + 0.9, o.z + 4.6);
  const bars: ReturnType<typeof box>[] = [];
  for (let i = 0; i < 10; i++) {
    const a = (i / 10) * Math.PI * 2;
    bars.push(translate(cylinder(0.05, 0.05, 2.0, 5, [0.22, 0.2, 0.2]), cage.x + Math.cos(a) * 0.75, cage.y + 1.0, cage.z + Math.sin(a) * 0.75));
  }
  bars.push(translate(cylinder(0.85, 0.85, 0.12, 10, [0.22, 0.2, 0.2]), cage.x, cage.y + 2.0, cage.z));
  bars.push(translate(cylinder(0.9, 0.95, 0.16, 10, [0.3, 0.27, 0.25]), cage.x, cage.y + 0.02, cage.z));
  st.addCylinder(cage.x, cage.z, 0.8, cage.y - 1, cage.y + 2.2, 'cage');
  ctx.spawn('dunes.plinth', new Transform(), combine(plinth, ...bars));

  // dark rocks along ridge lines
  const rocks: Instance[] = [];
  for (let i = 0; i < 40; i++) {
    const a = rng.range(0, 6.3), r = rng.range(10, b.radius - 6);
    const x = b.center.x + Math.cos(a) * r, z = b.center.z + Math.sin(a) * r;
    if (Math.hypot(x - o.x, z - o.z) < 10 || Math.hypot(x - SPOTS.whiteKeyBuried.x, z - SPOTS.whiteKeyBuried.z) < 8) continue;
    if (Math.abs(x) < 8 && z > -140) continue;
    const inst = jitterInstance(rng, new Vec3(x, h(x, z) - 0.4, z), 0.3);
    rocks.push(inst);
    st.addCylinder(x, z, 1.1 * (inst.scale as number), h(x, z) - 1, h(x, z) + 1.2, 'rock');
  }
  instanced(ctx, 'dunes.rocks', boulderMesh(P[2], 1.3), rocks);

  // a faint disturbed patch where the White Key is buried (readable only up close)
  const bk = SPOTS.whiteKeyBuried;
  const patch = translate(cylinder(1.4, 1.6, 0.12, 9, P[1]), bk.x, h(bk.x, bk.z) + 0.02, bk.z);
  ctx.spawn('dunes.patch', new Transform(), patch, { castShadow: false });

  let acc = 0;
  return {
    cageCenter: cage,
    update(playerPos: Vec3, particles: Particles, dt: number): void {
      acc += dt;
      if (acc < 0.08) return;
      acc = 0;
      // sand streams across the surface downwind (+X)
      for (let i = 0; i < 3; i++) {
        const x = playerPos.x + rng.range(-22, 22), z = playerPos.z + rng.range(-22, 22);
        const y = h(x, z) + rng.range(0.1, 0.5);
        particles.mote(_p.set(x, y, z), _v.set(rng.range(5, 9), rng.range(-0.2, 0.4), rng.range(-1.5, 1.5)), rng.range(1.2, 2.2), 0.09, [0.98, 0.92, 0.72], 0.2, 0);
      }
    },
  };
}
const _p = new Vec3(), _v = new Vec3();
